/**
 * CORE-586 — pure decision helpers for the Live-session entry screen.
 *
 * The screen itself is a React component and the repo has no hook/component
 * renderer in its Jest setup (`testEnvironment: 'node'`, `*.test.ts` only), so
 * the two pieces of the CORE-586 UPD that are easy to get wrong live here as
 * pure functions — the same reason `subscribeAppForeground` was extracted from
 * `useLiveCapabilities`:
 *
 *   1. classifying the room-fetch error, so a `capability_required` 403 shows an
 *      honest «недоступна в этой сессии» instead of the generic «приватная»;
 *   2. deciding whether the background refetch (foreground + poll) should keep
 *      running, so a terminal session (cancelled/ended/gone/denied) stops
 *      hammering `getRoomByCode`.
 *
 * Nothing here derives a right from a role or level — the capability copy is
 * read straight off the refusal body via `capabilityDeniedMessage` (CORE-819).
 */
import { isCapabilityDenied, capabilityDeniedMessage } from '../../../services/errorMessages';

/**
 * Screen load phases. `denied` carries an `EntryDenied` for its subtitle.
 * CORE-1778 — `agapella`: the session is included in the Agapēlla subscription
 * and the server refused this visitor; the panel opens the paywall instead of
 * the bare «Доступ закрыт».
 */
export type LoadPhase = 'loading' | 'reconnecting' | 'notfound' | 'denied' | 'signin' | 'agapella' | 'ready';

/** Why the door is closed, and the exact user-facing subtitle for it. */
export interface EntryDenied {
  /** `private` = invite-only session; `capability` = frozen snapshot refusal. */
  kind: 'private' | 'capability';
  message: string;
}

export interface EntryLoadOutcome {
  phase: LoadPhase;
  denied?: EntryDenied;
}

/** Default «доступ закрыт» subtitle — an invite-only session, not a capability. */
export const PRIVATE_DENIED_SUBTITLE =
  'Эта сессия приватная — вход только по приглашению организатора.';

function errStatus(e: any): number | undefined {
  return e?.status ?? e?.response?.status;
}

/**
 * CORE-2061 — сессия «только подписчикам»: карточку гостю сервер не отдаёт (403
 * с кодом входа CORE-1754, `LIVE_SUBSCRIBERS_ONLY_CODE`; строка повторена, чтобы
 * модуль остался чистым). Вошедшему он отдаёт карточку-замок, так что этот текст —
 * запасной.
 */
const LIVE_SUBSCRIBERS_ONLY = 'live_subscribers_only';
export const SUBSCRIBERS_DENIED_SUBTITLE =
  'Эта Live-сессия — для подписчиков ведущего. Подпишитесь, чтобы присоединиться.';

/** CORE-1778 — the code `agapellaGate` names; inlined to keep this module pure. */
const AGAPELLA_SUBSCRIPTION_REQUIRED = 'agapella_subscription_required';

function errCode(e: any): unknown {
  return e?.code ?? e?.Code ?? e?.response?.data?.code ?? e?.response?.data?.Code;
}

/** CORE-1778 — the panel title for the `agapella` phase. */
export const AGAPELLA_ENTRY_TITLE = 'Эта Live-сессия входит в Agapēlla';

/**
 * Map a `getRoomByCode` failure to a screen phase. Splits the 403 the way the
 * UPD requires: a `capability_required` refusal (the session's frozen snapshot
 * says «нет», CORE-819) gets the honest capability copy; any other 403/401 is
 * the plain invite-only «доступ закрыт». 404/410 → link gone; a signed-out
 * viewer on a 401 is sent to sign-in; everything else is transient → reconnect.
 *
 * `reconnecting` here means «keep the current snapshot / offer retry»; the
 * caller decides whether to also log it and whether a silent background refetch
 * should swallow it (a network blip must not blank an already-loaded screen).
 */
export function classifyEntryLoadError(
  err: unknown,
  opts: { isGuest: boolean },
): EntryLoadOutcome {
  const s = errStatus(err);
  if (s === 404 || s === 410) return { phase: 'notfound' };
  if (s === 403 && errCode(err) === AGAPELLA_SUBSCRIPTION_REQUIRED) return { phase: 'agapella' };
  // CORE-2061 — гостю подписаться нечем: сначала войти, дальше замок с подпиской.
  if (s === 403 && errCode(err) === LIVE_SUBSCRIBERS_ONLY) {
    return opts.isGuest
      ? { phase: 'signin' }
      : { phase: 'denied', denied: { kind: 'private', message: SUBSCRIBERS_DENIED_SUBTITLE } };
  }
  if (isCapabilityDenied(err)) {
    return { phase: 'denied', denied: { kind: 'capability', message: capabilityDeniedMessage(err) } };
  }
  if (s === 403) {
    return { phase: 'denied', denied: { kind: 'private', message: PRIVATE_DENIED_SUBTITLE } };
  }
  if (s === 401) {
    return opts.isGuest
      ? { phase: 'signin' }
      : { phase: 'denied', denied: { kind: 'private', message: PRIVATE_DENIED_SUBTITLE } };
  }
  return { phase: 'reconnecting' };
}

/**
 * Whether the background refetch (foreground reload + ~45s poll) should keep
 * running. Only a fully-loaded, non-terminal session is worth re-reading: a
 * cancelled or server-confirmed-ended room can no longer change in a way the
 * visitor can act on, and the non-`ready` phases (denied/notfound/sign-in) are
 * terminal by construction. A `Scheduled` room stays pollable on purpose — it
 * is the state that must flip to the green room the moment the host starts.
 */
export function shouldKeepPollingEntry(input: {
  phase: LoadPhase;
  isCancelled: boolean;
  isEnded: boolean;
}): boolean {
  return input.phase === 'ready' && !input.isCancelled && !input.isEnded;
}

/** Poll cadence — matched to `useLiveCapabilities`' REFRESH_MS so an open entry
 *  screen re-reads the session snapshot on the same rhythm rights refresh on. */
export const ENTRY_REFRESH_MS = 45_000;
